import {
  ClientSideRowModelModule,
  ModuleRegistry,
  PaginationModule,
  SideBarDef,
  ServerSideRowModelApiModule,
  type ColDef,
  type Theme,
} from 'ag-grid-enterprise';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { AgGridTableHeader, SearchBar, type AgGridTableHeaderProps } from 'Components/molecules';
import { normalizeLastUpdatedAt } from 'Core-utils/helpers/pageHints';
import { Typography } from 'Components/atoms';
import classNames from 'classnames';
import { agGridAmpDarkTheme } from 'Core-utils/agGridTheme';
import React, { ChangeEvent, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_BASE_COL_DEF,
  DEFAULT_GRID_SIZING,
  DEFAULT_GRID_SIDEBAR,
  DEFAULT_PAGINATION,
  type AgGridBasePaginationConfig,
  type AgGridBaseUrlSyncConfig,
} from './AgGridBase.config';
import { useAgGridUrlSync } from './useAgGridUrlSync';
import { SEARCH_PLACEHOLDER } from 'Core-utils/constants/constants';
import _ from 'lodash';

const agGridLicenseKey = process.env.REACT_APP_AG_GRID_LICENSE_KEY;
if (agGridLicenseKey) {
  LicenseManager.setLicenseKey(agGridLicenseKey);
}

let clientModulesRegistered = false;
let serverApiModuleRegistered = false;
let paginationModuleRegistered = false;

function ensureServerSideApiModuleRegistered() {
  if (!serverApiModuleRegistered) {
    ModuleRegistry.registerModules([ServerSideRowModelApiModule]);
    serverApiModuleRegistered = true;
  }
}

function ensureClientRowModelRegistered() {
  if (!clientModulesRegistered) {
    ModuleRegistry.registerModules([ClientSideRowModelModule]);
    clientModulesRegistered = true;
  }
}

function ensurePaginationModuleRegistered() {
  if (!paginationModuleRegistered) {
    ModuleRegistry.registerModules([PaginationModule]);
    paginationModuleRegistered = true;
  }
}

function resolvePaginationConfig(
  pagination: boolean | AgGridBasePaginationConfig | undefined
): AgGridBasePaginationConfig | null {
  if (!pagination) {
    return null;
  }
  if (pagination === true) {
    return {};
  }
  return pagination;
}

export type AgGridBaseRootProps = {
  children: React.ReactNode;
  size?: {
    height?: string;
    minHeight?: string;
  };
  fullWidth?: boolean;
};

/**
 * Wraps the grid and its children in a flex container with a fixed height and width.
 * The wrapper is used to apply a fixed height and width to the grid and its children.
 * @param children - The children to wrap.
 * @param className - The class name to apply to the wrapper.
 * @param size - The size of the wrapper.
 * @param fullWidth - Whether to make the wrapper full width.
 * @returns The wrapped grid.
 */
function AgGridBaseRoot({ children, size, fullWidth }: AgGridBaseRootProps) {
  return (
    <div
      className={classNames('ag-findings-grid',
        'd-flex',
        'flex-column',
        'h-100',
        'pb-4',
        fullWidth && 'ag-findings-grid--full-width',
        'ag-grid-theme--amp-dark')}
        style={{
          minHeight: size?.minHeight,
        }}
      >
      {children}
    </div>
  );
}

export type AgGridBaseHeaderProps = AgGridTableHeaderProps & {
  hidden?: boolean;
  rowCount?: number | null;
  totalCount?: number | null;
};

/**
 * The header is used to display the title and the count of the grid.
 * @param hidden - Whether to hide the header.
 * @param rowCount - The number of rows in the grid.
 * @param totalCount - The total number of rows in the grid.
 * @param rightExtras - The extra elements to display on the right side of the header.
 * @param rest - The rest of the props to pass to the header.
 * @returns The header.
 */
function AgGridBaseHeader({
  hidden,
  rowCount,
  totalCount,
  lastUpdatedAt,
  rightExtras,
  title,
  actionItems,
  hideActions,
  leftExtras,
  className,
}: AgGridBaseHeaderProps) {
  const countExtras =
    typeof rowCount === 'number' && typeof totalCount === 'number' ? (
      <Typography variant="body10" color="text-low-emphasis" className="ag-grid-table-header__results-count">
        <span className="ag-grid-table-header__results-count-number">{rowCount}</span> of{' '}
        <span className="ag-grid-table-header__results-count-number">{totalCount}</span> results
      </Typography>
    ) : null;

  const showMainHeader = !hidden;
  const validatedLastUpdatedAt = normalizeLastUpdatedAt(lastUpdatedAt);
  const showFreshness = Boolean(validatedLastUpdatedAt);

  if (!showMainHeader && !showFreshness) {
    return null;
  }

  return (
    <AgGridTableHeader
      title={showMainHeader ? title : undefined}
      leftExtras={showMainHeader ? leftExtras : undefined}
      actionItems={showMainHeader ? actionItems : undefined}
      hideActions={hideActions || !showMainHeader}
      className={className}
      lastUpdatedAt={validatedLastUpdatedAt}
      rightExtras={
        showMainHeader ? (
          <>
            {rightExtras}
            {countExtras}
          </>
        ) : undefined
      }
    />
  );
}

function AgGridBaseSearchBar({ onChange, defaultValue }: { onChange: (event: ChangeEvent<HTMLInputElement>) => void, defaultValue: string | undefined }) {
  const [inputValue, setInputValue] = useState(defaultValue ?? '');
  useEffect(() => {
    setInputValue(defaultValue ?? '');
  }, [defaultValue]);

  const onChangeCallback = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    onChange(event);
  }, [onChange]);

  return (
    <div className="ag-grid-search-bar table-subheader d-flex gap-3 p-0 mb-2 flex-shrink-0">
      <SearchBar
      value={inputValue}
      onChange={onChangeCallback}
      placeholder={SEARCH_PLACEHOLDER}
      searchClassName="search-container__input-cont"
      />
    </div>
  );
}

export type AgGridBaseTableProps<TData> = {
  /**
   * Declares every visible column: field/column id, header text, width, filters, sort, and how each cell is rendered.
   * AG Grid uses this array as the source of truth for headers and cells; it maps to the native `columnDefs` prop.
   */
  columnDefs: ColDef<TData>[];

  /**
   * Which row model the grid uses (how data is loaded and held in memory).
   * - `client`: all rows are supplied up front via `rowData` (good for smaller, static lists).
   * - `server`: rows are loaded on demand through `serverSideDatasource` (server-side row model / SSRM; good for large or paged data).
   */
  dataMode: 'client' | 'server';

  /**
   * The full dataset when `dataMode` is `client`. Each object is one row; column `field` / `valueGetter` read properties from it.
   * Ignored when `dataMode` is `server` (use `serverSideDatasource` instead).
   */
  rowData?: TData[];

  /**
   * Callback object that fetches blocks of rows when `dataMode` is `'server'`.
   * Implement `getRows` to call your API and pass rows back via `params.success()` / `params.fail()`; the grid calls it as the user scrolls or filters.
   * Use a stable reference (e.g. `useStableServerSideDatasource`) so the grid does not reload unnecessarily on unrelated re-renders.
   */
  serverSideDatasource?: AgGridReactProps<TData>['serverSideDatasource'];

  /**
   * Default column behavior applied to every column unless a column overrides it (e.g. `resizable`, `sortable`, `filter`, `minWidth`).
   * Merged with this component’s baseline defaults and with `agGridProps.defaultColDef` (consumer wins last).
   */
  defaultColDef?: ColDef<TData>;

  /**
   * Optional AG Grid v33+ theme object (Quartz-based skins from `Core-utils/agGridTheme`).
   * If omitted, `agGridAmpDarkTheme` is used so consumers usually do not need to pass this.
   */
  theme?: Theme;

  /**
   * When `true`, shows the columns tool panel (see `DEFAULT_GRID_SIDEBAR` in `AgGridBase.config.ts`).
   * When `false` or omitted, the sidebar is hidden (`sideBar={false}` on AG Grid).
   * Override the panel definition with `agGridProps.sideBar` when this is `true`.
   */
  showSidebar?: boolean;

  /**
   * Extra CSS classes on the outer `div` that wraps `AgGridReact` (typically `ag-theme-alpine` is already applied).
   * Use for layout (e.g. findings `ag-findings-grid__wrapper`) or theme helper classes that style the grid surface.
   */
  wrapperClassName?: string;

  /**
   * CSS height of the grid viewport (the scrollable area). Controls how tall the table appears on the page.
   * Use `'auto'` when using `domLayout: 'autoHeight'` in `agGridProps` so the grid grows with row count.
   * If omitted, a shared default height from `DEFAULT_GRID_SIZING` is used.
   */
  bodyHeight?: string;

  /**
   * CSS minimum height of that same wrapper. Useful to avoid a collapsed grid when content is short or during loading.
   * If omitted, `DEFAULT_GRID_SIZING.minHeight` applies.
   */
  bodyMinHeight?: string;

  /**
   * Optional bridge between filter/sort state and the URL: hydrate once from query params, then write back on changes (debounced).
   * Consumers supply `adapter.fromUrl` / `adapter.toUrl` and optional `getUrlState` / `setUrlState`; domain field names stay in the feature layer.
   */
  urlSync?: AgGridBaseUrlSyncConfig;

  /**
   * Escape hatch for any other AG Grid option: `onGridReady`, `context`, `getRowId`, `sideBar`, `rowSelection`, event handlers, etc.
   * Spread last, so these override overlapping defaults set by `AgGridBase.Table` (except `defaultColDef`, which is merged as documented above).
   */
  agGridProps?: Partial<AgGridReactProps<TData>>;

  /**
   * Whether to allow the columns to be moved. If omitted, the columns are movable.
   */
  movableColumns?: boolean;

  /**
   * Whether to allow the search bar to be used. If omitted, the search bar is not used.
   */
  showSearchBar?: boolean;

  /**
   * The default value for the search bar. If provided, the search bar will be pre-filled with this value.
   */
  searchDefaultValue?: string | undefined;

  /**
   * The callback to handle the search change.
   */
  onSearchChange?: (searchValue: string) => void;

  /**
   * When `true` or a config object, enables AG Grid's built-in pagination panel instead of
   * infinite scroll. For `dataMode="server"`, also sets SSRM cache options so only the active
   * page block is retained (`maxBlocksInCache` defaults to 1).
   */
  pagination?: boolean | AgGridBasePaginationConfig;
};

/**
 * The table is used to display the grid.
 * @param columnDefs - The column definitions for the grid.
 * @param dataMode - The data mode for the grid.
 * @param rowData - The row data for the grid.
 * @param serverSideDatasource - The server side datasource for the grid.
 * @param defaultColDef - The default column definition for the grid.
 * @param theme - The theme for the grid.
 * @param wrapperClassName - The class name to apply to the wrapper.
 * @param bodyHeight - The height of the grid viewport.
 * @param bodyMinHeight - The minimum height of the grid viewport.
 * @param urlSync - The URL sync configuration for the grid.
 * @param agGridProps - The AG Grid props for the grid.
 * @param showSidebar - Whether to show the sidebar.
 * @param movableColumns - Whether to allow the columns to be moved.
 * @param showSearchBar - Whether to show the search bar.
 * @param searchDefaultValue - The default value for the search bar.
 * @param onSearchChange - The callback to handle the search change.
 * @param ref - The ref to the grid.
 * @returns The grid.
 */
const AgGridBaseTableInner = <TData,>(
  {
    columnDefs,
    dataMode,
    rowData,
    serverSideDatasource,
    defaultColDef,
    theme,
    wrapperClassName,
    bodyHeight,
    bodyMinHeight,
    urlSync,
    agGridProps,
    showSidebar,
    movableColumns = true,
    showSearchBar = false,
    searchDefaultValue,
    onSearchChange,
    pagination,
  }: AgGridBaseTableProps<TData>,
  ref: React.ForwardedRef<AgGridReact<TData>>
) => {
  const [gridReadySignal, setGridReadySignal] = useState(0);
  const innerRef = React.useRef<AgGridReact<TData> | null>(null);
  const currentSearchValueRef = useRef<string | undefined>(undefined);
  const setRefs = useCallback(
    (instance: AgGridReact<TData> | null) => {
      innerRef.current = instance;
      if (typeof ref === 'function') {
        ref(instance);
      } else if (ref) {
        (ref as React.MutableRefObject<AgGridReact<TData> | null>).current = instance;
      }
    },
    [ref]
  );

  const gridRefForUrl = useMemo(
    () =>
      ({
        get current() {
          return innerRef.current;
        },
      }) as React.RefObject<{ api: import('ag-grid-community').GridApi<TData> | null } | null>,
    []
  );

  const { onFilterOrSortChanged, flushUrlSync } = useAgGridUrlSync(
    gridRefForUrl,
    urlSync,
    gridReadySignal
  );

  // Needed for api.refreshServerSide used by URL sync in SSRM grids.
  ensureServerSideApiModuleRegistered();

  if (dataMode === 'client') {
    ensureClientRowModelRegistered();
  }

  const paginationConfig = resolvePaginationConfig(pagination);
  if (paginationConfig) {
    ensurePaginationModuleRegistered();
  }

  const paginationGridOptions = useMemo(() => {
    if (!paginationConfig) {
      return {};
    }

    const pageSize = paginationConfig.pageSize ?? DEFAULT_PAGINATION.pageSize;
    const pageSizeSelector = paginationConfig.pageSizeSelector ?? [...DEFAULT_PAGINATION.pageSizeSelector];
    const panels = paginationConfig.panels;

    const options: Partial<AgGridReactProps<TData>> = {
      pagination: true,
      paginationPageSize: pageSize,
      paginationPageSizeSelector: pageSizeSelector,
      suppressScrollOnNewData: true,
      ...(panels ? { paginationPanels: panels } : {}),
    };

    if (dataMode === 'server') {
      const maxBlocksInCache = paginationConfig.maxBlocksInCache ?? DEFAULT_PAGINATION.maxBlocksInCache;
      options.cacheBlockSize = pageSize;
      options.maxBlocksInCache = maxBlocksInCache;
      options.serverSideInitialRowCount = pageSize;
    }

    return options;
  }, [dataMode, paginationConfig]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (gridReadySignal === 0) {
      return;
    }
    if (currentSearchValueRef.current === event.target.value) {
      return;
    }
    currentSearchValueRef.current = event.target.value;
    onSearchChange?.(currentSearchValueRef.current);
  }, [agGridProps, gridReadySignal, onFilterOrSortChanged]);

  const handleGridReady = useCallback(
    (event: Parameters<NonNullable<AgGridReactProps<TData>['onGridReady']>>[0]) => {
      agGridProps?.onGridReady?.(event);
      setGridReadySignal((n) => n + 1);
    },
    [agGridProps]
  );

  const handleFilterChanged = useCallback(
    (event: Parameters<NonNullable<AgGridReactProps<TData>['onFilterChanged']>>[0]) => {
      agGridProps?.onFilterChanged?.(event);
      onFilterOrSortChanged();
    },
    [agGridProps, onFilterOrSortChanged]
  );

  const handleSortChanged = useCallback(
    (event: Parameters<NonNullable<AgGridReactProps<TData>['onSortChanged']>>[0]) => {
      agGridProps?.onSortChanged?.(event);
      onFilterOrSortChanged();
    },
    [agGridProps, onFilterOrSortChanged]
  );

  useEffect(
    () => () => {
      flushUrlSync();
    },
    [flushUrlSync]
  );

  const {
    onGridReady: _og,
    onFilterChanged: _of,
    onSortChanged: _os,
    rowModelType: _rm,
    rowData: _rd,
    serverSideDatasource: _ssd,
    defaultColDef: agGridDefaultColDef,
    headerHeight: overrideHeaderHeight,
    rowHeight: overrideRowHeight,
    domLayout: overrideDomLayout,
    sideBar: overrideSideBar,
    ...restAgGridProps
  } = agGridProps ?? {};

  const mergedDefaultColDef = useMemo((): ColDef<TData> => {
    return {
      ...(DEFAULT_BASE_COL_DEF as ColDef<TData>),
      ...defaultColDef,
      ...agGridDefaultColDef,
    };
  }, [agGridDefaultColDef, defaultColDef]);

  const wrapperClass = classNames(
    'ag-theme-alpine',
    'flex-grow-1',
    wrapperClassName ??
      'ag-findings-grid__wrapper ag-findings-grid__wrapper--theme-findings ag-grid-theme--amp-dark'
  );

  return (
    <div
      className={wrapperClass}
      style={{
        height:
          bodyHeight === 'auto' ? 'auto' : bodyHeight ?? DEFAULT_GRID_SIZING.defaultHeight,
        minHeight: bodyMinHeight ?? DEFAULT_GRID_SIZING.minHeight,
      }}
    >
      {showSearchBar && <AgGridBase.SearchBar onChange={handleSearchChange} defaultValue={searchDefaultValue}/>}
      <AgGridReact<TData>
        ref={setRefs}
        theme={theme ?? agGridAmpDarkTheme}
        columnDefs={columnDefs}
        rowModelType={dataMode === 'server' ? 'serverSide' : 'clientSide'}
        rowData={dataMode === 'client' ? rowData : undefined}
        serverSideDatasource={dataMode === 'server' ? serverSideDatasource : undefined}
        defaultColDef={mergedDefaultColDef}
        headerHeight={overrideHeaderHeight ?? DEFAULT_GRID_SIZING.headerHeight}
        rowHeight={overrideRowHeight ?? DEFAULT_GRID_SIZING.rowHeight}
        domLayout={overrideDomLayout ?? 'normal'}
        sideBar={showSidebar ? overrideSideBar ?? DEFAULT_GRID_SIDEBAR as SideBarDef : false}
        onGridReady={handleGridReady}
        onFilterChanged={handleFilterChanged}
        onSortChanged={handleSortChanged}
        suppressMovableColumns={movableColumns ? undefined : true}
        {...paginationGridOptions}
        {...restAgGridProps}
      />
    </div>
  );
};

const AgGridBaseTable = forwardRef(AgGridBaseTableInner) as <TData>(
  props: AgGridBaseTableProps<TData> & { ref?: React.ForwardedRef<AgGridReact<TData>> }
) => React.ReactElement | null;

export const AgGridBase = {
  Root: AgGridBaseRoot,
  Header: AgGridBaseHeader,
  SearchBar: AgGridBaseSearchBar,
  Table: AgGridBaseTable,
};

export default AgGridBase;
